import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Alert, IconButton, Tooltip } from "@mui/material";
import {
  CurrencyRupeeOutlined,
  DeleteOutline,
  HelpOutlineOutlined,
  Inventory2Outlined,
  TaskAltOutlined,
  VerifiedOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import DropdownBtnOutlined from "../../../components/Dashboard/DropdownButtons/DropdownBtnOutlined/DropdownBtnOutlined";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";
import { API_ENDPOINT } from "../../../services/api/resources";
import { DOCUMENT_IMAGE_UPLOAD_ACCEPT } from "../../../utils/documentUploadTypes";

import classes from "./AssetRegisterTab.module.css";

const DISPOSAL_STATUS_OPTIONS = [
  "1 Year",
  "2 Year",
  "3 Year",
  "4 Year",
  "5 Year",
  "Other",
];

const safeStr = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeStr(value) || "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeDateTimeValue = (value) =>
  safeStr(value)
    .trim()
    .replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2}|$))/, ".$1");

const formatCreatedDate = (value) => {
  if (!value) return "-";
  const normalizedValue = normalizeDateTimeValue(value);
  if (!normalizedValue) return "-";

  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return safeStr(value) || "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeAssetIds = (assetIdValue) => {
  if (!assetIdValue) return [];
  if (Array.isArray(assetIdValue)) return assetIdValue.filter(Boolean);
  if (typeof assetIdValue === "object") {
    return Object.values(assetIdValue).filter(Boolean);
  }
  return [safeStr(assetIdValue)].filter(Boolean);
};

const parseMoney = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") return value;
  const cleaned = safeStr(value).replace(/,/g, "").trim();
  if (!cleaned) return NaN;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const formatMoney = (value) => {
  const amount = parseMoney(value);
  if (!Number.isFinite(amount)) return "-";
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const resolveUnitPrice = (...sources) => {
  for (const source of sources) {
    const value = pickFirstValue(source, [
      "unit_price",
      "unitPrice",
      "unit_cost",
      "unitCost",
      "rate",
      "parsed_rate",
    ]);
    const formatted = formatMoney(value);
    if (formatted !== "-") return formatted;
  }
  return "-";
};

const PriceWithIcon = ({ value, className, iconClassName }) => {
  const formatted = safeStr(value).trim() || "-";
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        whiteSpace: "nowrap",
      }}
    >
      <CurrencyRupeeOutlined
        className={iconClassName}
        sx={{ fontSize: 18, color: "#075b97" }}
      />
      <span>{formatted}</span>
    </span>
  );
};

const formatDeliveryAddress = (address) => {
  const a = address || {};
  const parts = [
    safeStr(a.shipping_address1).trim(),
    safeStr(a.shipping_address2).trim(),
    safeStr(a.city).trim(),
    safeStr(a.state).trim(),
    safeStr(a.pincode).trim(),
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
};

const toCsvValue = (value) => {
  const v = safeStr(value ?? "");
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
};

const getInvoiceNumber = (invoice) => {
  const inv = invoice || {};
  return (
    inv?.invoice_number ||
    inv?.invoice_no ||
    inv?.invoiceNo ||
    inv?.invoiceNumber ||
    "-"
  );
};

const getCreatedDateValue = (register) => {
  const r = register || {};
  return (
    r?.asset_created_date ||
    r?.asset?.asset_created_date ||
    r?.created_date ||
    r?.created_on ||
    r?.created_at ||
    r?.grn?.grn_date ||
    "-"
  );
};

const normalizeName = (value) =>
  safeStr(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const pickFirstValue = (obj, keys) => {
  if (!obj) return "";
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== null && value !== undefined && safeStr(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const getLineItemMatchKey = (obj) =>
  safeStr(
    pickFirstValue(obj, [
      "po_item_id",
      "purchase_order_item_id",
      "po_line_id",
      "line_id",
      "item_id",
      "id",
    ])
  ).trim();

const normalizeText = (value) =>
  safeStr(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const wordSet = (value) => {
  const words = normalizeText(value)
    .split(" ")
    .map((w) => w.trim())
    .filter(Boolean)
    .filter((w) => w.length >= 3);
  return new Set(words);
};

const intersectionSize = (a, b) => {
  let count = 0;
  for (const v of a) if (b.has(v)) count += 1;
  return count;
};

const scoreLineItemMatch = (row, target) => {
  const rowName = normalizeName(row?.item_name);
  const rowDesc = normalizeText(row?.description);
  const targetName = normalizeName(target?.name);
  const targetDesc = normalizeText(target?.description);

  let score = 0;

  if (targetName && rowName) {
    if (rowName === targetName) score += 100;
    else if (rowName.includes(targetName) || targetName.includes(rowName)) score += 60;
    else {
      const a = wordSet(rowName);
      const b = wordSet(targetName);
      const overlap = intersectionSize(a, b);
      if (overlap > 0) score += overlap * 8;
    }
  }

  if (targetDesc && rowDesc) {
    if (rowDesc === targetDesc) score += 80;
    else if (rowDesc.includes(targetDesc) || targetDesc.includes(rowDesc)) score += 40;
    else {
      const a = wordSet(rowDesc);
      const b = wordSet(targetDesc);
      const overlap = intersectionSize(a, b);
      if (overlap > 0) score += overlap * 5;
    }
  }

  if (targetName && rowDesc && rowDesc.includes(targetName)) score += 20;
  if (targetDesc && rowName && targetDesc.includes(rowName)) score += 10;

  return score;
};

const findBestLineItemMatch = (items, target) => {
  const list = Array.isArray(items) ? items : [];
  if (!normalizeText(target?.name) && !normalizeText(target?.description)) return null;

  let best = null;
  let bestScore = 0;
  for (const row of list) {
    const score = scoreLineItemMatch(row, target);
    if (score > bestScore) {
      bestScore = score;
      best = row;
    }
  }
  return bestScore >= 20 ? best : null;
};

const extractPolicyCandidate = (responseObj) => {
  const entries = [];
  const walk = (value, path) => {
    if (value == null) return;
    if (typeof value === "string") {
      const s = value.trim();
      if (s) entries.push({ path, value: s });
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v, idx) => walk(v, `${path}[${idx}]`));
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([k, v]) => walk(v, path ? `${path}.${k}` : k));
    }
  };
  walk(responseObj, "");

  const byKey = entries.find((e) =>
    /insurance.*policy|policy.*number|policy_no|policyNumber/i.test(e.path),
  );
  if (byKey) return byKey.value;

  const matchFromText = (text) => {
    const m = text.match(
      /(insurance\s*policy\s*(no\.?|number)|policy\s*(no\.?|number))\s*[:#-]?\s*([A-Z0-9][A-Z0-9\-\/]{4,})/i,
    );
    return m?.[4] || "";
  };

  for (const e of entries) {
    const m = matchFromText(e.value);
    if (m) return m;
  }

  const token = entries
    .map((e) => e.value)
    .join(" ")
    .match(/\b[A-Z0-9][A-Z0-9\-\/]{7,}\b/i);
  return token?.[0] || "";
};

const AssetRegisterTab = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token;
  const buyerToken =
    loginData?.buyer_token ||
    loginData?.user?.buyer_token ||
    loginData?.user?.token ||
    loginData?.organization_token ||
    loginData?.user_token;

  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [assetSummary, setAssetSummary] = useState(null);
  const [assetSummaryLoading, setAssetSummaryLoading] = useState(false);
  const [assetSummaryError, setAssetSummaryError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [detailSearchQuery, setDetailSearchQuery] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [disposalStatusSaved, setDisposalStatusSaved] = useState("");
  const [disposalStatusSelection, setDisposalStatusSelection] = useState("");
  const [disposalStatusOther, setDisposalStatusOther] = useState("");
  const [disposalStatusSaving, setDisposalStatusSaving] = useState(false);
  const [disposalStatusError, setDisposalStatusError] = useState("");
  const [disposalStatusSuccess, setDisposalStatusSuccess] = useState(false);

  const [insurancePolicyNumberSaved, setInsurancePolicyNumberSaved] = useState("");
  const [insurancePolicyNumberDraft, setInsurancePolicyNumberDraft] = useState("");
  const [insurancePolicyFile, setInsurancePolicyFile] = useState(null);
  const [insurancePolicyExtracting, setInsurancePolicyExtracting] = useState(false);
  const [insurancePolicySaving, setInsurancePolicySaving] = useState(false);
  const [insurancePolicyError, setInsurancePolicyError] = useState("");
  const [insurancePolicyExtractSuccess, setInsurancePolicyExtractSuccess] = useState(false);
  const [insurancePolicySaveSuccess, setInsurancePolicySaveSuccess] = useState(false);

  const insurancePolicyFileInputRef = useRef(null);

  const [warrantyCardFileNameSaved, setWarrantyCardFileNameSaved] = useState("");
  const [warrantyCardFile, setWarrantyCardFile] = useState(null);
  const [warrantyCardError, setWarrantyCardError] = useState("");
  const [warrantyCardExtracting, setWarrantyCardExtracting] = useState(false);
  const [warrantyCardExtractSuccess, setWarrantyCardExtractSuccess] = useState(false);
  const warrantyCardFileInputRef = useRef(null);

  const [warrantyReferenceNumberSaved, setWarrantyReferenceNumberSaved] = useState("");
  const [warrantyReferenceNumberDraft, setWarrantyReferenceNumberDraft] = useState("");
  const [warrantyReferenceKey, setWarrantyReferenceKey] = useState("warranty_number");
  const [warrantyReferenceSaving, setWarrantyReferenceSaving] = useState(false);
  const [warrantyReferenceSaveSuccess, setWarrantyReferenceSaveSuccess] = useState(false);
  const [warrantyReferenceError, setWarrantyReferenceError] = useState("");
  const [paginationMeta, setPaginationMeta] = useState({
    total_count: 0,
    page_count: 0,
    current_page_count: 0,
  });

  const [pageNumber, setPageNumber] = useState(1);

  const assetIdParam = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return safeStr(params.get("asset_id")).trim();
  }, [location.search]);

  const serverSearch = safeStr(assetIdParam || searchQuery).trim();
  const assetSummaryFetchKeyRef = useRef("");
  const assetRegisterRequestRef = useRef({ key: "", promise: null });

  const fetchAssetSummary = useCallback(async () => {
    if (assetIdParam) return; // list view only

    setAssetSummaryError("");
    if (!safeStr(buyerToken).trim() || !safeStr(authToken).trim()) {
      setAssetSummary(null);
      return;
    }

    setAssetSummaryLoading(true);
    try {
      const res = await axios.post(
        API_ENDPOINT.get_asset_summary,
        { buyer_token: buyerToken },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setAssetSummary(res?.data ?? null);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.response ||
        err?.message ||
        "Failed to load asset summary";
      setAssetSummary(null);
      setAssetSummaryError(String(msg));
    } finally {
      setAssetSummaryLoading(false);
    }
  }, [assetIdParam, authToken, buyerToken]);

  useEffect(() => {
    setDisposalStatusError("");
    setDisposalStatusSuccess(false);

    if (!assetIdParam) {
      setDisposalStatusSaved("");
      setDisposalStatusSelection("");
      setDisposalStatusOther("");
      return;
    }

    try {
      const key = `assetRegister.disposalStatus.${assetIdParam}`;
      const cached = safeStr(window?.localStorage?.getItem(key)).trim();
      if (cached) setDisposalStatusSaved(cached);
    } catch {
      // ignore storage errors
    }
  }, [assetIdParam]);

  useEffect(() => {
    setInsurancePolicyError("");
    setInsurancePolicyExtractSuccess(false);
    setInsurancePolicySaveSuccess(false);
    setInsurancePolicyFile(null);

    if (!assetIdParam) {
      setInsurancePolicyNumberSaved("");
      setInsurancePolicyNumberDraft("");
      return;
    }

    try {
      const key = `assetRegister.insurancePolicyNumber.${assetIdParam}`;
      const cached = safeStr(window?.localStorage?.getItem(key)).trim();
      if (cached) {
        setInsurancePolicyNumberSaved(cached);
        setInsurancePolicyNumberDraft(cached);
      } else {
        setInsurancePolicyNumberSaved("");
        setInsurancePolicyNumberDraft("");
      }
    } catch {
      // ignore storage errors
    }
  }, [assetIdParam]);

  useEffect(() => {
    setWarrantyCardError("");
    setWarrantyCardExtractSuccess(false);
    setWarrantyCardFile(null);

    if (!assetIdParam) {
      setWarrantyCardFileNameSaved("");
      return;
    }

    try {
      const key = `assetRegister.warrantyCardFileName.${assetIdParam}`;
      const cached = safeStr(window?.localStorage?.getItem(key)).trim();
      setWarrantyCardFileNameSaved(cached || "");
    } catch {
      // ignore storage errors
    }
  }, [assetIdParam]);

  useEffect(() => {
    setWarrantyReferenceError("");
    setWarrantyReferenceSaveSuccess(false);

    if (!assetIdParam) {
      setWarrantyReferenceNumberSaved("");
      setWarrantyReferenceNumberDraft("");
      setWarrantyReferenceKey("warranty_number");
      return;
    }

    setWarrantyReferenceNumberSaved("");
    setWarrantyReferenceNumberDraft("");
    setWarrantyReferenceKey("warranty_number");

    try {
      const valueKey = `assetRegister.warrantyReferenceNumber.${assetIdParam}`;
      const cachedValue = safeStr(window?.localStorage?.getItem(valueKey)).trim();
      if (cachedValue) {
        setWarrantyReferenceNumberSaved(cachedValue);
        setWarrantyReferenceNumberDraft(cachedValue);
      }

      const keyKey = `assetRegister.warrantyReferenceKey.${assetIdParam}`;
      const cachedKey = safeStr(window?.localStorage?.getItem(keyKey)).trim();
      if (cachedKey === "warranty_number" || cachedKey === "license_number") {
        setWarrantyReferenceKey(cachedKey);
      }
    } catch {
      // ignore storage errors
    }
  }, [assetIdParam]);

  const handleExtractWarrantyOrLicenseDetails = useCallback(async () => {
    if (!assetIdParam) return;

    setWarrantyCardError("");
    setWarrantyCardExtractSuccess(false);

    if (!warrantyCardFile) {
      setWarrantyCardError("Please upload the warranty/licence card file first.");
      return;
    }

    setWarrantyCardExtracting(true);
    try {
      const nextName = safeStr(warrantyCardFile?.name).trim();
      if (nextName) {
        setWarrantyCardFileNameSaved(nextName);
        try {
          const key = `assetRegister.warrantyCardFileName.${assetIdParam}`;
          window?.localStorage?.setItem(key, nextName);
        } catch {
          // ignore storage errors
        }
      }

      const formData = new FormData();
      formData.append("file", warrantyCardFile);
      formData.append("org_token", buyerToken);

      const extractRes = await axios.post(
        API_ENDPOINT.extract_warranty_or_license_details,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );

      const raw = extractRes?.data ?? {};
      const warrantyNumber = safeStr(
        raw?.warranty_number ?? raw?.response?.warranty_number,
      ).trim();
      const licenseNumber = safeStr(
        raw?.license_number ?? raw?.response?.license_number,
      ).trim();

      const candidate = warrantyNumber || licenseNumber;
      if (!candidate) {
        setWarrantyCardError(
          "Could not extract a warranty/licence number. Please try another file.",
        );
        return;
      }

      const nextKey = warrantyNumber ? "warranty_number" : "license_number";
      setWarrantyReferenceKey(nextKey);
      setWarrantyReferenceNumberDraft(candidate);

      setWarrantyCardExtractSuccess(true);
      window.setTimeout(() => setWarrantyCardExtractSuccess(false), 1500);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to extract warranty/licence details.";
      setWarrantyCardError(msg);
    } finally {
      setWarrantyCardExtracting(false);
    }
  }, [assetIdParam, authToken, warrantyCardFile]);

  const handleSaveWarrantyReferenceNumber = useCallback(async () => {
    if (!assetIdParam) return;

    setWarrantyReferenceError("");
    setWarrantyReferenceSaveSuccess(false);

    const nextValue = safeStr(warrantyReferenceNumberDraft).trim();
    if (!nextValue) {
      setWarrantyReferenceError("Please extract the warranty/licence number first.");
      return;
    }

    const keyToSend =
      warrantyReferenceKey === "license_number" ? "license_number" : "warranty_number";

    setWarrantyReferenceSaving(true);
    try {
      await axios.post(
        API_ENDPOINT.get_asset_register,
        {
          buyer_token: buyerToken,
          asset_id: assetIdParam,
          [keyToSend]: nextValue,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
    } catch {
      // ignore backend failures; still persist locally
    } finally {
      setWarrantyReferenceNumberSaved(nextValue);
      try {
        const valueKey = `assetRegister.warrantyReferenceNumber.${assetIdParam}`;
        window?.localStorage?.setItem(valueKey, nextValue);
        const keyKey = `assetRegister.warrantyReferenceKey.${assetIdParam}`;
        window?.localStorage?.setItem(keyKey, keyToSend);
      } catch {
        // ignore storage errors
      }
      setWarrantyReferenceSaveSuccess(true);
      window.setTimeout(() => setWarrantyReferenceSaveSuccess(false), 1500);
      setWarrantyReferenceSaving(false);
    }
  }, [
    assetIdParam,
    authToken,
    buyerToken,
    warrantyReferenceKey,
    warrantyReferenceNumberDraft,
  ]);

  const normalizePageNumber = useCallback(
    (value) => {
      const n = parseInt(value || 1, 10);
      if (!Number.isFinite(n) || n < 1) return 1;
      return n;
    },
    [],
  );

  const pageNumberInt = useMemo(
    () => (assetIdParam ? 1 : normalizePageNumber(pageNumber)),
    [assetIdParam, normalizePageNumber, pageNumber],
  );

  const fetchAssetRegister = useCallback(async () => {
    setErrorMessage("");
    if (!safeStr(buyerToken).trim()) {
      setErrorMessage("Buyer token is missing. Please re-login and try again.");
      setRegisters([]);
      setPaginationMeta({ total_count: 0, page_count: 0, current_page_count: 0 });
      return;
    }
    if (!safeStr(authToken).trim()) {
      setErrorMessage("Auth token is missing. Please re-login and try again.");
      setRegisters([]);
      setPaginationMeta({ total_count: 0, page_count: 0, current_page_count: 0 });
      return;
    }

    const requestPayload = {
      buyer_token: buyerToken,
      search: serverSearch || undefined,
      page: pageNumberInt,
    };
    const requestKey = JSON.stringify({
      authToken,
      buyerToken,
      search: serverSearch,
      page: pageNumberInt,
    });
    if (
      assetRegisterRequestRef.current.key === requestKey &&
      assetRegisterRequestRef.current.promise
    ) {
      return assetRegisterRequestRef.current.promise;
    }

    setLoading(true);
    const request = axios.post(API_ENDPOINT.get_asset_register, requestPayload, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    assetRegisterRequestRef.current = { key: requestKey, promise: request };
    try {
      const res = await request;
      if (assetRegisterRequestRef.current.key !== requestKey) return;
      const data = res?.data;
      const pagination = data?.pagination || data?.meta || {};
      const list =
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.records) && data.records) ||
        (Array.isArray(data?.response) && data.response) ||
        [];
      setRegisters(list);

      const total_count = Number(
        pagination?.total_count ??
          pagination?.totalCount ??
          pagination?.total_items ??
          pagination?.totalItems ??
          data?.total_count ??
          data?.totalCount ??
          data?.total_items ??
          data?.total ??
          list.length ??
          0,
      );
      const current_page_count = Number(
        pagination?.current_page_count ??
          pagination?.currentPageCount ??
          data?.current_page_count ??
          data?.currentPageCount ??
          list.length ??
          0,
      );
      const derivedPageSize = Math.max(
        Number.isFinite(current_page_count) && current_page_count > 0
          ? current_page_count
          : list.length || 10,
        1,
      );
      const page_count = Number(
        pagination?.page_count ??
          pagination?.pageCount ??
          pagination?.total_pages ??
          pagination?.totalPages ??
          data?.page_count ??
          data?.pageCount ??
          data?.total_pages ??
          data?.totalPages ??
          (total_count > 0 ? Math.ceil(total_count / derivedPageSize) : 0),
      );
      setPaginationMeta({
        total_count: Number.isFinite(total_count) ? total_count : 0,
        page_count: Number.isFinite(page_count) && page_count > 0 ? page_count : 0,
        current_page_count: Number.isFinite(current_page_count)
          ? current_page_count
          : list.length || 0,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load asset register";
      setErrorMessage(msg);
      setRegisters([]);
      setPaginationMeta({ total_count: 0, page_count: 0, current_page_count: 0 });
    } finally {
      if (assetRegisterRequestRef.current.key === requestKey) {
        assetRegisterRequestRef.current = { key: "", promise: null };
        setLoading(false);
      }
    }
  }, [authToken, buyerToken, pageNumberInt, serverSearch]);

  useEffect(() => {
    const id = setTimeout(() => {
      fetchAssetRegister();
    }, 300);
    return () => clearTimeout(id);
  }, [fetchAssetRegister]);

  useEffect(() => {
    if (assetIdParam) return;

    const org = safeStr(buyerToken).trim();
    const auth = safeStr(authToken).trim();
    if (!org || !auth) return;

    // React 18 StrictMode runs effects twice in dev; avoid double API calls.
    const key = `${org}|${auth}`;
    if (assetSummaryFetchKeyRef.current === key) return;
    assetSummaryFetchKeyRef.current = key;
    fetchAssetSummary();
  }, [assetIdParam, authToken, fetchAssetSummary, buyerToken]);

  const headings = [
    { label: "Asset ID", field: "asset_id", col_width: "210px", noWrap: true,sortable: false },
    { label: "Item Code", field: "item_code", col_width: "150px", noWrap: true ,sortable: false},
    {
      label: "Item Name",
      field: "item_name",
      col_width: "320px",
      cellType: "readMore",
      maxLength: 70,
      sortable: false
    },
    {
      label: "Description",
      field: "description",
      col_width: "360px",
      cellType: "readMore",
      maxLength: 80,
      sortable: false
    },
    // {
    //   label: "Price",
    //   field: "price",
    //   col_width: "120px",
    //   noWrap: true,
    //   sortable: false,
    //   renderCell: (row) => <PriceWithIcon value={row?.price} />,
    // },
    {
      label: "Price",
      field: "unit_price",
      col_width: "130px",
      noWrap: true,
      sortable: false,
      renderCell: (row) => <PriceWithIcon value={row?.unit_price} />,
    },
    {
      label: "Book Value",
      field: "book_value",
      col_width: "140px",
      noWrap: true,
      sortable: false,
      renderCell: (row) => <PriceWithIcon value={row?.book_value} />,
    },
    { label: "HSN Code", field: "hsn_code", col_width: "140px", noWrap: true ,sortable: false},
    { label: "UNSPSC Code", field: "unspsc_code", col_width: "190px", noWrap: true,sortable: false },
    {
      label: "Created Date",
      field: "created_date",
      col_width: "200px",
      noWrap: true,
      renderCell: (row) => <span>{row?.created_date || "-"}</span>,

    },
   
  ];

  const assetRows = useMemo(() => {
    const list = Array.isArray(registers) ? registers : [];
    const rows = [];

    list.forEach((register, regIndex) => {
      const po = register?.purchase_order || {};
      const invoice = register?.invoice || {};
      const grn = register?.grn || {};
      const asset = register?.asset || {};

      const items = Array.isArray(grn?.items) ? grn.items : [];
      const createdDate = formatCreatedDate(getCreatedDateValue(register));
      const invoiceNumber = safeStr(getInvoiceNumber(invoice)) || "-";

      if (items.length === 0) {
        const fallbackBookValue = formatMoney(
          pickFirstValue(register, [
            "book_value",
            "bookValue",
            "total_book_value_inr",
            "asset_book_value",
          ]) ||
            pickFirstValue(asset, [
              "book_value",
              "bookValue",
              "total_book_value_inr",
              "asset_book_value",
            ])
        );
        rows.push({
          id: `${regIndex}-0-0`,
          asset_id: "-",
          grn_no: grn?.grn_no ?? "-",
          item_code: "-",
          model_number: "-",
          serial_number: "-",
          gl_code: "-",
          project_code: "-",
          cost_center_code: "-",
          item_name: "-",
          description: "-",
          price: "-",
          unit_price: "-",
          book_value: fallbackBookValue,
          hsn_code: "-",
          unspsc_code: "-",
          po_number: po?.po_number ?? "-",
          invoice_number: invoiceNumber,
          created_date: createdDate,
          __rawRegister: register,
          __rawItem: null,
          __assetIds: [],
        });
        return;
      }

      items.forEach((item, itemIndex) => {
        const assetIds = normalizeAssetIds(item?.asset_id);
        const rawGrnName = safeStr(item?.item_name).trim();
        const rawGrnDesc = safeStr(
          pickFirstValue(item, [
            "description",
            "item_description",
            "itemDescription",
            "material_description",
            "materialDescription",
          ])
        ).trim();

        const likelyDescOnly = !rawGrnDesc && rawGrnName.length > 40;
        const matchTarget = {
          name: likelyDescOnly ? "" : rawGrnName,
          description: rawGrnDesc || (likelyDescOnly ? rawGrnName : ""),
        };

        const invoiceItems =
          Array.isArray(invoice?.items) && invoice.items.length > 0
            ? invoice.items
            : safeStr(invoice?.item_name).trim() || safeStr(invoice?.description).trim()
              ? [
                  {
                    item_name: invoice?.item_name,
                    description: invoice?.description,
                    qty: invoice?.qty,
                    amount: invoice?.amount,
                    unit_price: invoice?.unit_price,
                    hsn: invoice?.hsn,
                    hsn_code: invoice?.hsn_code,
                    unspsc_code: invoice?.unspsc_code,
                  },
                ]
              : [];

        const poItems =
          Array.isArray(po?.items) && po.items.length > 0
            ? po.items
            : safeStr(po?.item_name).trim() || safeStr(po?.description).trim()
              ? [
                  {
                    item_name: po?.item_name,
                    description: po?.description,
                    qty: po?.qty,
                    amount: po?.amount,
                    unit_price: po?.unit_price,
                  },
                ]
              : [];

        const matchingInvoiceItem = findBestLineItemMatch(invoiceItems, matchTarget);
        const matchingPoItem = findBestLineItemMatch(poItems, matchTarget);
        const fallbackPoItem = !matchingPoItem && poItems.length === 1 ? poItems[0] : null;
        const fallbackInvoiceItem =
          !matchingInvoiceItem && invoiceItems.length === 1 ? invoiceItems[0] : null;

        const displayItemName =
          safeStr(pickFirstValue(matchingInvoiceItem, ["item_name"])) ||
          safeStr(pickFirstValue(matchingPoItem, ["item_name"])) ||
          (likelyDescOnly ? "-" : rawGrnName) ||
          "-";

        const displayDescription =
          rawGrnDesc ||
          safeStr(pickFirstValue(matchingInvoiceItem, ["description"])) ||
          safeStr(pickFirstValue(matchingPoItem, ["description"])) ||
          (likelyDescOnly ? rawGrnName : "") ||
          "-";

        const itemCode =
          safeStr(
            pickFirstValue(matchingPoItem || fallbackPoItem, [
              "item_code",
              "itemCode",
              "item_id",
              "itemId",
              "code",
            ]) ||
              pickFirstValue(matchingInvoiceItem || fallbackInvoiceItem, [
                "item_code",
                "itemCode",
                "code",
              ]) ||
              pickFirstValue(item, ["item_code", "itemCode", "code"])
          ).trim() || "-";

        const lineAmount =
          pickFirstValue(matchingInvoiceItem, ["amount"]) ||
          (invoiceItems.length <= 1 ? invoice?.amount : "") ||
          pickFirstValue(matchingPoItem, ["amount"]) ||
          pickFirstValue(item, ["amount"]);

        const divisor =
          Number(assetIds.length) ||
          Number(pickFirstValue(item, ["received_quantity", "ordered_quantity", "qty"])) ||
          Number(pickFirstValue(matchingInvoiceItem, ["qty"])) ||
          0;

        const total = parseMoney(lineAmount);
        const perAsset =
          Number.isFinite(total) && divisor > 0 ? total / divisor : total;
        const price = formatMoney(perAsset);
        const unitPrice = resolveUnitPrice(
          matchingInvoiceItem || fallbackInvoiceItem,
          matchingPoItem || fallbackPoItem,
          item,
          register?.financial_details,
          register?.asset?.financial_details,
        );
        const bookValue = formatMoney(
          pickFirstValue(register, [
            "book_value",
            "bookValue",
            "total_book_value_inr",
            "asset_book_value",
          ]) ||
            pickFirstValue(asset, [
              "book_value",
              "bookValue",
              "total_book_value_inr",
              "asset_book_value",
            ]) ||
            pickFirstValue(item, ["book_value", "bookValue"]),
        );

        const hsnCode =
          safeStr(
            pickFirstValue(matchingInvoiceItem, [
              "hsn_code",
              "hsn",
              "hsn_sac",
              "hsnSac",
            ])
          ) || "-";
        const unspscCode =
          safeStr(
            pickFirstValue(matchingInvoiceItem, ["unspsc_code", "unspsc", "unspscCode"])
          ) || "-";

        const modelNumber =
          safeStr(
            pickFirstValue(matchingPoItem || fallbackPoItem, [
              "model_number",
              "model_no",
              "modelNumber",
              "modelNo",
              "model",
            ]) ||
              pickFirstValue(item, ["model_number", "model_no", "modelNumber", "modelNo"]) ||
              pickFirstValue(asset, ["model_number", "model_no", "modelNumber", "modelNo"]),
          ).trim() || "-";

        const serialNumber =
          safeStr(
            pickFirstValue(item, [
              "serial_number",
              "serial_no",
              "serialNumber",
              "serialNo",
              "serial",
            ]) ||
              pickFirstValue(asset, [
                "serial_number",
                "serial_no",
                "serialNumber",
                "serialNo",
              ]),
          ).trim() || "-";

        const glCode =
          safeStr(
            pickFirstValue(matchingPoItem || fallbackPoItem, [
              "gl_code",
              "glCode",
              "gl_code_value",
              "glCodeValue",
              "itemGl_code",
              "item_gl_code",
            ]) ||
              pickFirstValue(item, ["gl_code", "glCode"]) ||
              pickFirstValue(asset, ["gl_code", "glCode"]),
          ).trim() || "-";

        const projectCode =
          safeStr(
            pickFirstValue(matchingPoItem || fallbackPoItem, [
              "project_code",
              "projectCode",
              "project_code_value",
              "projectCodeValue",
              "itemProject_code",
              "item_project_code",
            ]) ||
              pickFirstValue(item, ["project_code", "projectCode"]) ||
              pickFirstValue(asset, ["project_code", "projectCode"]),
          ).trim() || "-";

        const costCenterCode =
          safeStr(
            pickFirstValue(matchingPoItem || fallbackPoItem, [
              "cost_center_code",
              "cost_center",
              "costCenterCode",
              "costCenter",
              "cost_center_code_value",
              "costCenterCodeValue",
            ]) ||
              pickFirstValue(item, ["cost_center_code", "cost_center", "costCenterCode"]) ||
              pickFirstValue(asset, ["cost_center_code", "cost_center", "costCenterCode"]),
          ).trim() || "-";

        if (assetIds.length === 0) {
          rows.push({
            id: `${regIndex}-${itemIndex}-0`,
            asset_id: "-",
            grn_no: grn?.grn_no ?? "-",
            item_code: itemCode,
            model_number: modelNumber,
            serial_number: serialNumber,
            gl_code: glCode,
            project_code: projectCode,
            cost_center_code: costCenterCode,
            item_name: displayItemName,
            description: displayDescription,
            price,
            unit_price: unitPrice,
            book_value: bookValue,
            hsn_code: hsnCode,
            unspsc_code: unspscCode,
            po_number: po?.po_number ?? "-",
            invoice_number: invoiceNumber,
            created_date: createdDate,
            __rawRegister: register,
            __rawItem: item,
            __assetIds: [],
          });
          return;
        }

        assetIds.forEach((assetId, assetIndex) => {
          rows.push({
            id: `${regIndex}-${itemIndex}-${assetIndex}`,
            asset_id: safeStr(assetId) || "-",
            grn_no: grn?.grn_no ?? "-",
            item_code: itemCode,
            model_number: modelNumber,
            serial_number: serialNumber,
            gl_code: glCode,
            project_code: projectCode,
            cost_center_code: costCenterCode,
            item_name: displayItemName,
            description: displayDescription,
            price,
            unit_price: unitPrice,
            book_value: bookValue,
            hsn_code: hsnCode,
            unspsc_code: unspscCode,
            po_number: po?.po_number ?? "-",
            invoice_number: invoiceNumber,
            created_date: createdDate,
            __rawRegister: register,
            __rawItem: item,
            __assetIds: assetIds,
          });
        });
      });
    });

    return rows;
  }, [registers]);

  const selectedRow = useMemo(() => {
    if (!assetIdParam) return null;
    return (
      assetRows.find((row) => safeStr(row?.asset_id).trim() === assetIdParam) ||
      null
    );
  }, [assetIdParam, assetRows]);

  const openDetails = useCallback(
    (row) => {
      const assetId = safeStr(row?.asset_id).trim();
      if (!assetId) return;
      navigate({
        pathname: location.pathname,
        search: `?asset_id=${encodeURIComponent(assetId)}`,
      });
    },
    [location.pathname, navigate],
  );

  const closeDetails = useCallback(() => {
    navigate({ pathname: location.pathname, search: "" });
  }, [location.pathname, navigate]);

  const details = useMemo(() => {
    if (!selectedRow?.__rawRegister) return null;

    const register = selectedRow.__rawRegister;
    const item = selectedRow.__rawItem || {};
    const po = register?.purchase_order || {};
    const invoice = register?.invoice || {};
    const grn = register?.grn || {};
    const asset = register?.asset || {};
    const payment = register?.payment || {};
    const license = register?.license_details_or_agreement || {};
    const warranty = register?.warranty_details || {};
    const warrantyReferenceBackend = safeStr(
      pickFirstValue(warranty, [
        "warranty_number",
        "warranty_no",
        "warranty_reference_number",
        "warrantyReferenceNumber",
      ]) ||
        pickFirstValue(license, [
          "license_number",
          "licence_number",
          "license_no",
          "licence_no",
          "licenseNumber",
          "licenceNumber",
        ]),
    ).trim();
    const warrantyReferenceValue =
      safeStr(warrantyReferenceNumberSaved).trim() ||
      safeStr(warrantyReferenceNumberDraft).trim() ||
      warrantyReferenceBackend ||
      "-";

    const deliveryAddressText = formatDeliveryAddress(po?.delivery_address);

    const poItems =
      Array.isArray(po?.items) && po.items.length > 0
        ? po.items
        : safeStr(po?.item_name).trim() || safeStr(po?.description).trim()
          ? [
              {
                item_name: po?.item_name,
                description: po?.description,
                qty: po?.qty,
                amount: po?.amount,
                unit_price: po?.unit_price,
              },
            ]
          : [];

    const invoiceItems =
      Array.isArray(invoice?.items) && invoice.items.length > 0
        ? invoice.items
        : safeStr(invoice?.item_name).trim() || safeStr(invoice?.description).trim()
          ? [
              {
                item_name: invoice?.item_name,
                description: invoice?.description,
                qty: invoice?.qty,
                amount: invoice?.amount,
                unit_price: invoice?.unit_price,
                hsn: invoice?.hsn,
                hsn_code: invoice?.hsn_code,
                unspsc_code: invoice?.unspsc_code,
              },
            ]
          : [];
    const assetIds = normalizeAssetIds(item?.asset_id);

    const selectedMatchKey = getLineItemMatchKey(item);
    const selectedTarget = {
      name: selectedRow?.item_name || item?.item_name,
      description:
        selectedRow?.description ||
        pickFirstValue(item, ["description", "item_description", "itemDescription"]) ||
        item?.item_name,
    };
    const invoiceItemsForSelected = invoiceItems.filter(
      (invItem) => scoreLineItemMatch(invItem, selectedTarget) >= 20
    );

    let poItemsForSelected = [];
    if (selectedMatchKey) {
      poItemsForSelected = poItems.filter(
        (poItem) => getLineItemMatchKey(poItem) === selectedMatchKey
      );
    }

    if (poItemsForSelected.length === 0) {
      poItemsForSelected = poItems.filter(
        (poItem) => scoreLineItemMatch(poItem, selectedTarget) >= 20
      );
    }

    const selectedPoItem = poItemsForSelected?.[0] || poItems?.[0] || {};
    const selectedInvoiceItem =
      invoiceItemsForSelected?.[0] || invoiceItems?.[0] || {};
    const selectedUnitPrice = resolveUnitPrice(
      selectedInvoiceItem,
      selectedPoItem,
      item,
      register?.financial_details,
      register?.asset?.financial_details,
    );

    const modelNumberValue =
      safeStr(
        pickFirstValue(selectedPoItem, [
          "model_number",
          "model_no",
          "modelNumber",
          "modelNo",
          "model",
        ]) ||
          pickFirstValue(item, ["model_number", "model_no", "modelNumber", "modelNo"]) ||
          pickFirstValue(asset, ["model_number", "model_no", "modelNumber", "modelNo"]),
      ).trim() || "-";

    const serialNumberValue =
      safeStr(
        pickFirstValue(item, [
          "serial_number",
          "serial_no",
          "serialNumber",
          "serialNo",
          "serial",
        ]) ||
          pickFirstValue(selectedPoItem, ["serial_number", "serial_no", "serialNumber", "serialNo"]) ||
          pickFirstValue(asset, ["serial_number", "serial_no", "serialNumber", "serialNo"]),
      ).trim() || "-";

    const costCenterCodeValue =
      safeStr(
        pickFirstValue(selectedPoItem, [
          "cost_center_code",
          "cost_center",
          "costCenterCode",
          "costCenter",
          "cost_center_code_value",
          "costCenterCodeValue",
        ]) ||
          pickFirstValue(po, ["cost_center_code", "costCenterCode", "cost_center"]) ||
          pickFirstValue(asset, ["cost_center_code", "costCenterCode", "cost_center"]),
      ).trim() || "-";

    const glCodeValue =
      safeStr(
        pickFirstValue(selectedPoItem, [
          "gl_code",
          "glCode",
          "gl_code_value",
          "glCodeValue",
          "itemGl_code",
          "item_gl_code",
        ]) ||
          pickFirstValue(po, ["gl_code", "glCode"]) ||
          pickFirstValue(asset, ["gl_code", "glCode"]),
      ).trim() || "-";

    const projectCodeValue =
      safeStr(
        pickFirstValue(selectedPoItem, [
          "project_code",
          "projectCode",
          "project_code_value",
          "projectCodeValue",
          "itemProject_code",
          "item_project_code",
        ]) ||
          pickFirstValue(po, ["project_code", "projectCode"]) ||
          pickFirstValue(asset, ["project_code", "projectCode"]),
      ).trim() || "-";

    const poCreatedDateRaw = pickFirstValue(po, [
      "po_created_date",
      "po_date",
      "purchase_order_date",
      "created_date",
      "created_at",
    ]);

    const poCurrency =
      safeStr(
        pickFirstValue(po, ["currency_type", "currency", "currencyType"]) ||
          pickFirstValue(poItemsForSelected?.[0], ["currency_type", "currency"]) ||
          pickFirstValue(invoice, ["currency_type", "currency"]) ||
          pickFirstValue(invoiceItemsForSelected?.[0], ["currency_type", "currency"])
      ).trim() || "-";

    const vendorCode =
      safeStr(
        pickFirstValue(po, ["vendor_code", "vendorCode", "vendor_id", "vendorId"])
      ).trim() || "-";

    const procurementFields = [
      { label: "Requisition ID", value: po?.pr_number || "-" },
      { label: "PO ID", value: po?.po_number || "-" },
      { label: "PO Date", value: formatDate(poCreatedDateRaw) },
      { label: "Supplier Name", value: invoice?.vendor_name || "-" },
      { label: "Supplier ID", value: vendorCode },
      { label: "Currency", value: poCurrency },
      { label: "Unit Price", value: selectedUnitPrice, cellType: "price" },
    ];

    const grnFields = [
      { label: "GRN Number", value: grn?.grn_no || "-" },
      { label: "GRN Date", value: formatDate(grn?.grn_date) },

      { label: "Expected Delivery Date", value: formatDate(grn?.delivery_date) },
      { label: "Receiving Location", value: deliveryAddressText },
      { label: "Delivery Location", value: deliveryAddressText },
    ];

    const warrantyFields = [
      {
        label: "Warranty / Licence Card",
        value: safeStr(warrantyCardFileNameSaved).trim() || "-",
        cellType: "warrantyCard",
      },
      {
        label: "Reference Number",
        value: warrantyReferenceValue,
        cellType: "warrantyReferenceNumber",
      },
      { label: "Start Date", value: formatDate(warranty?.warranty_start_date) },
      { label: "End Date", value: formatDate(warranty?.warranty_end_date) },
      { label: "AMC Provider", value: warranty?.amc_provider || "-" },
      { label: "Type", value: warranty?.warranty_type || "-" },
      { label: "Period", value: warranty?.warranty_period || "-" },
    ];

    const invoiceFields = [
      { label: "Invoice Number", value: getInvoiceNumber(invoice) },
      { label: "Invoice Date", value: formatDate(invoice?.invoice_date) },
      { label: "Unit Price", value: selectedUnitPrice, cellType: "price" },
      { label: "HSN/SAC Code", value: selectedRow?.hsn_code || "-" },
      { label: "Department", value: po?.department || "-" },
      { label: "Cost Center Code", value: costCenterCodeValue },
      { label: "GL Code", value: glCodeValue },
      { label: "Project Code", value: projectCodeValue }
    ];

    const derivedFields = [
      {
        label: "Asset Lifecycle Status",
        value:
          warranty?.warranty_lifecycle_status ||
          asset?.asset_lifecycle_status ||
          "-",
      },
      {
        label: "Warranty Remaining Days",
        value: safeStr(warranty?.warranty_remaining_days) || "-",
      },
      { label: "Duplicate Asset Detection", value: "-" },
      { label: "Asset Validation Status", value: "-" },
    ];

    const allocationFields = [
      { label: "Asset Owner", value: asset?.asset_owner || "-" },
    
      { label: "Delivery Location", value: deliveryAddressText },
      { label: "Requestor", value: asset?.requestor || "-" },
    ];

    const auditFields = [
      {
        label: "Insurance Policy Copy",
        value: insurancePolicyFile?.name || "-",
        cellType: "insurancePolicyCopy",
      },
      {
        label: "Insurance Policy Number",
        value:
          safeStr(insurancePolicyNumberSaved).trim() ||
          safeStr(
            pickFirstValue(asset, [
              "insurance_policy_number",
              "insurance_policy_no",
              "insurancePolicyNumber",
              "insurancePolicyNo",
              "policy_number",
              "policyNo",
            ]) ||
              pickFirstValue(register, ["insurance_policy_number", "insurancePolicyNumber"]),
          ).trim() ||
          "-",
        cellType: "insurancePolicyNumber",
      },
      { label: "Audit Status", value: "-" },
      { label: "Compliance Status", value: "-" },
      {
        label: "Disposal Status",
        value:
          safeStr(disposalStatusSaved).trim() ||
          safeStr(
            pickFirstValue(asset, [
              "disposal_status",
              "disposalStatus",
              "disposal_status_text",
              "disposal_status_years",
              "disposal",
            ]) ||
              pickFirstValue(register, ["disposal_status", "disposalStatus"])
          ).trim() ||
          "-",
        cellType: "disposalStatus",
      },
    ];

    const itemDetailsRows = [
      {
        id: 1,
        item_name: item?.item_name ?? "-",
        assets_count: assetIds.length,
      },
    ];

    const assetFields = [
      { label: "Asset ID", value: selectedRow?.asset_id || "-" },
      {
  label: "Asset Item",
  value: selectedRow?.item_name
    ? selectedRow.item_name.charAt(0).toUpperCase() + selectedRow.item_name.slice(1)
    : "-"
},
      { label: "Asset Category", value: asset?.asset_category || "-" },
     
      { label: "OEM / Brand", value: asset?.brand || "-" },
      { label: "Model Number", value: modelNumberValue },
       { label: "Asset Description", value: selectedRow?.description || "-" },
      { label: "Serial Number", value: serialNumberValue },
      {
        label: "SKU / Manufacturer Part Number",
        value:
          safeStr(
            pickFirstValue(poItemsForSelected?.[0], ["part_number", "partNumber"]) ||
              selectedRow?.item_code
          ).trim() || "-",
      },
      { label: "Asset Owner", value: asset?.asset_owner || "-" },
      { label: "Requestor", value: asset?.requestor || "-" },
    ];

    const selectedAssetId = safeStr(selectedRow?.asset_id).trim();
    const assetIdRows = selectedAssetId
      ? [{ id: 1, asset_id: selectedAssetId, selected: "Yes" }]
      : [];

    return {
      assetFields,
      procurementFields,
      grnFields,
      invoiceFields,
      warrantyFields,
      derivedFields,
      allocationFields,
      auditFields,
      poItems: poItemsForSelected,
      poItemsEmptyMessage:
        poItemsForSelected.length === 0 && poItems.length > 0
          ? "No PO line items found for this selected item."
          : "",
      invoiceItems: invoiceItemsForSelected,
      invoiceItemsEmptyMessage:
        invoiceItemsForSelected.length === 0 && invoiceItems.length > 0
          ? "No invoice line items found for this selected item."
          : "",
      itemDetailsRows,
      assetIdRows,
      grn,
      po,
      invoice,
    };
  }, [
    disposalStatusSaved,
    insurancePolicyNumberSaved,
    selectedRow,
    warrantyCardFileNameSaved,
    warrantyReferenceNumberDraft,
    warrantyReferenceNumberSaved,
  ]);

  useEffect(() => {
    if (!assetIdParam || !details) return;

    const current = safeStr(
      (details.auditFields || []).find((f) => f?.label === "Disposal Status")?.value
    ).trim();
    const normalized = current && current !== "-" ? current : "";
    const normalizedCompact = normalized.replace(/\s+/g, "").toLowerCase();

    if (!normalized) {
      setDisposalStatusSelection("");
      setDisposalStatusOther("");
      return;
    }

    const found = DISPOSAL_STATUS_OPTIONS.find((opt) => {
      const optCompact = opt.replace(/\s+/g, "").toLowerCase();
      return optCompact === normalizedCompact;
    });
    if (found && found !== "Other") {
      setDisposalStatusSelection(found);
      setDisposalStatusOther("");
      return;
    }

    setDisposalStatusSelection("Other");
    // For "Other", store only the numeric years so the input stays clean (e.g. "7" from "7 Year").
    const yearsOnly = normalized.replace(/\D/g, "");
    setDisposalStatusOther(yearsOnly || normalized);
  }, [assetIdParam, details]);

  useEffect(() => {
    if (!assetIdParam || !details) return;

    // Seed insurance policy number from backend details if available and nothing cached yet.
    if (safeStr(insurancePolicyNumberSaved).trim()) return;

    const current = safeStr(
      (details.auditFields || []).find((f) => f?.label === "Insurance Policy Number")?.value,
    ).trim();

    if (current && current !== "-") {
      setInsurancePolicyNumberDraft(current);
    }
  }, [assetIdParam, details, insurancePolicyNumberSaved]);

  useEffect(() => {
    if (!assetIdParam || !selectedRow?.__rawRegister) return;
    if (safeStr(warrantyReferenceNumberSaved).trim()) return;

    const register = selectedRow.__rawRegister;
    const warranty = register?.warranty_details || {};
    const license = register?.license_details_or_agreement || {};

    const backendWarranty = safeStr(
      pickFirstValue(warranty, [
        "warranty_number",
        "warranty_no",
        "warranty_reference_number",
        "warrantyReferenceNumber",
      ]),
    ).trim();
    const backendLicense = safeStr(
      pickFirstValue(license, [
        "license_number",
        "licence_number",
        "license_no",
        "licence_no",
        "licenseNumber",
        "licenceNumber",
      ]),
    ).trim();

    const seededValue = backendWarranty || backendLicense;
    if (!seededValue) return;

    setWarrantyReferenceNumberDraft(seededValue);
    if (backendWarranty) setWarrantyReferenceKey("warranty_number");
    else if (backendLicense) setWarrantyReferenceKey("license_number");
  }, [assetIdParam, selectedRow, warrantyReferenceNumberSaved]);

  const handleSaveDisposalStatus = useCallback(() => {
    if (!assetIdParam) return;

    setDisposalStatusError("");
    setDisposalStatusSuccess(false);

    const selection = safeStr(disposalStatusSelection).trim();
    const otherYearsRaw = safeStr(disposalStatusOther).trim();
    const otherYearsDigits = otherYearsRaw.replace(/\D/g, "");
    const otherYearsInt = otherYearsDigits ? parseInt(otherYearsDigits, 10) : NaN;
    const finalValue =
      selection === "Other"
        ? Number.isFinite(otherYearsInt) && otherYearsInt > 0
          ? `${otherYearsInt} Year`
          : ""
        : safeStr(selection).trim();

    if (!finalValue) {
      setDisposalStatusError("Please select a disposal status.");
      return;
    }

    if (selection === "Other") {
      if (!Number.isFinite(otherYearsInt) || otherYearsInt <= 0) {
        setDisposalStatusError("Please enter the number of years (e.g. 7).");
        return;
      }
    }

    setDisposalStatusSaving(true);
    const persistLocal = () => {
      setDisposalStatusSaved(finalValue);
      try {
        const key = `assetRegister.disposalStatus.${assetIdParam}`;
        window?.localStorage?.setItem(key, finalValue);
      } catch {
        // ignore storage errors
      }
    };

    (async () => {
      try {
        // Backend support is unclear; send the value along with get_asset_register payload (best-effort).
        await axios.post(
          API_ENDPOINT.get_asset_register,
          {
            buyer_token: buyerToken,
            asset_id: assetIdParam,
            disposal_status: finalValue,
          },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
      } catch {
        // ignore backend failures; still persist locally
      } finally {
        persistLocal();
        setDisposalStatusSuccess(true);
        window.setTimeout(() => setDisposalStatusSuccess(false), 1500);
        setDisposalStatusSaving(false);
      }
    })();
  }, [assetIdParam, authToken, disposalStatusOther, disposalStatusSelection, buyerToken]);

  const handleExtractInsurancePolicyNumber = useCallback(async () => {
    if (!assetIdParam) return;
    setInsurancePolicyError("");
    setInsurancePolicyExtractSuccess(false);

    if (!insurancePolicyFile) {
      setInsurancePolicyError("Please upload the insurance policy copy first.");
      return;
    }

    setInsurancePolicyExtracting(true);
    try {
      const formData = new FormData();
      // New extraction endpoint dedicated to insurance policy numbers.
      formData.append("file", insurancePolicyFile);
      formData.append("org_token", buyerToken);

      const extractRes = await axios.post(API_ENDPOINT.extract_insurance_policy_number, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const raw = extractRes?.data ?? {};
      const candidate = safeStr(
        raw?.insurance_policy_number ?? raw?.response?.insurance_policy_number ?? extractPolicyCandidate(raw),
      ).trim();
      if (!candidate) {
        setInsurancePolicyError("Could not extract an insurance policy number. Please enter it manually.");
        return;
      }

      setInsurancePolicyNumberDraft(candidate);
      setInsurancePolicyExtractSuccess(true);
      window.setTimeout(() => setInsurancePolicyExtractSuccess(false), 1500);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to extract insurance policy number.";
      setInsurancePolicyError(msg);
    } finally {
      setInsurancePolicyExtracting(false);
    }
  }, [assetIdParam, authToken, insurancePolicyFile]);

  const handleSaveInsurancePolicyNumber = useCallback(async () => {
    if (!assetIdParam) return;

    setInsurancePolicyError("");
    setInsurancePolicySaveSuccess(false);

    const nextValue = safeStr(insurancePolicyNumberDraft).trim();
    if (!nextValue) {
      setInsurancePolicyError("Please enter the insurance policy number.");
      return;
    }

    setInsurancePolicySaving(true);
    try {
      // Best-effort: include value in get_asset_register payload if backend supports persistence.
      await axios.post(
        API_ENDPOINT.get_asset_register,
        {
          buyer_token: buyerToken,
          asset_id: assetIdParam,
          insurance_policy_number: nextValue,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
    } catch {
      // ignore backend failures; still persist locally
    } finally {
      setInsurancePolicyNumberSaved(nextValue);
      try {
        const key = `assetRegister.insurancePolicyNumber.${assetIdParam}`;
        window?.localStorage?.setItem(key, nextValue);
      } catch {
        // ignore storage errors
      }
      setInsurancePolicySaveSuccess(true);
      window.setTimeout(() => setInsurancePolicySaveSuccess(false), 1500);
      setInsurancePolicySaving(false);
    }
  }, [assetIdParam, authToken, insurancePolicyNumberDraft, buyerToken]);

  const sheetSections = useMemo(() => {
    if (!details) return [];

    const coreFields = (details.assetFields || []).filter(
      (f) => f?.label !== "Asset Owner" && f?.label !== "Requestor",
    );

    const selectedItemRow = (details.itemDetailsRows || [])[0] || {};
    const goodsReceiptItemFields = [
      { label: "Item Name", value: selectedItemRow?.item_name ?? "-" },
      { label: "Assets Count", value: selectedItemRow?.assets_count ?? "-" },
    ];

    return [
      { title: "Asset Identity", fields: coreFields },
      { title: "Procurement", fields: details.procurementFields || [] },
      {
        title: "Goods Receipt",
        fields: [...(details.grnFields || []), ...goodsReceiptItemFields],
      },
      { title: "Warranty & Licence Service", fields: details.warrantyFields || [] },
      { title: "Invoice & Finance", fields: details.invoiceFields || [] },
      { title: "Asset Life Cycle", fields: details.derivedFields || [] },
      { title: "Allocation & Ownership", fields: details.allocationFields || [] },
      { title: "Audit & Compliance", fields: details.auditFields || [] },
    ].filter((s) => Array.isArray(s.fields) && s.fields.length > 0);
  }, [details]);

  const visibleSheetSections = useMemo(() => {
    const q = safeStr(detailSearchQuery).trim().toLowerCase();
    if (!q) return sheetSections;

    return sheetSections
      .map((section) => {
        const title = safeStr(section?.title);
        const titleMatch = title.toLowerCase().includes(q);
        const fields = (Array.isArray(section?.fields) ? section.fields : []).filter(
          (f) => {
            const label = safeStr(f?.label).toLowerCase();
            const value = safeStr(f?.value).toLowerCase();
            return titleMatch || label.includes(q) || value.includes(q);
          },
        );
        return { ...section, fields };
      })
      .filter((section) => section.fields.length > 0);
  }, [detailSearchQuery, sheetSections]);

  const exportDetails = useCallback(
    async (outputFormat) => {
      if (!details) return;
      const fmt = safeStr(outputFormat).toLowerCase();
      if (fmt !== "csv" && fmt !== "pdf") return;

      setExportLoading(true);
      try {
        const rows = (sheetSections || []).flatMap((section) =>
          (section.fields || []).map((f) => ({
            Section: safeStr(section.title) || "-",
            Field: safeStr(f?.label) || "-",
            Value: safeStr(f?.value) || "-",
          })),
        );

        const timestamp = new Date().toISOString().slice(0, 10);
        const fileBase = `Asset Register - ${assetIdParam || "Details"} - ${timestamp}`;

        if (fmt === "csv") {
          const header = ["Section", "Field", "Value"];
          const csv =
            `${header.join(",")}\n` +
            rows
              .map((r) =>
                [r.Section, r.Field, r.Value].map(toCsvValue).join(","),
              )
              .join("\n");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
          saveAs(blob, `${fileBase}.csv`);
          return;
        }

        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
        doc.setFontSize(12);
        doc.text(`Asset Register - ${assetIdParam || "-"}`, 40, 40);

        // eslint-disable-next-line no-undef
        doc.autoTable({
          startY: 60,
          head: [["Section", "Field", "Value"]],
          body: rows.map((r) => [r.Section, r.Field, r.Value]),
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
          headStyles: { fillColor: [7, 91, 151], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 120 },
            1: { cellWidth: 140 },
            2: { cellWidth: 250 },
          },
        });

        doc.save(`${fileBase}.pdf`);
      } finally {
        setExportLoading(false);
      }
    },
    [assetIdParam, details, sheetSections],
  );

  const summaryCards = useMemo(() => {
    const s = assetSummary || {};
    const toInt = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? n : 0;
    };

    const totalAssets = toInt(s?.total_assets);
    const totalValueLabel =
      safeStr(s?.total_value_inr_label).trim() ||
      (Number.isFinite(Number(s?.total_value_inr))
        ? `₹ ${Number(s.total_value_inr).toLocaleString("en-IN")}`
        : "-");

    const inWarranty = toInt(s?.in_warranty);
    const inWarrantyPct = toInt(s?.in_warranty_percentage);
    const outWarranty = toInt(s?.out_of_warranty);
    const outWarrantyPct = toInt(s?.out_of_warranty_percentage);
    const activeAssets = toInt(s?.active_assets);
    const activeAssetsPct = toInt(s?.active_assets_percentage);
    const unknownWarranty = toInt(s?.unknown_warranty);
    const totalBookValue = safeStr(s?.total_book_value_inr).trim() || "-";

    return [
      {
        key: "total_assets",
        label: "Total Assets",
        value: totalAssets.toLocaleString("en-IN"),
        badge: "All",
        sub: "Across this org",
        color: "blue",
        icon: <Inventory2Outlined className={classes.kpiIcon} />,
      },
      {
        key: "total_value",
        label: "Total Value (INR)",
        value: <PriceWithIcon value={totalValueLabel} />,
        badge: "INR",
        sub: safeStr(s?.total_value_inr_formatted).trim()
          ? `₹ ${safeStr(s?.total_value_inr_formatted).trim()}`
          : "Total book value",
        color: "purple",
        icon: <CurrencyRupeeOutlined className={classes.kpiIcon} />,
      },
      {
        key: "book_value",
        label: "Book Value (INR)",
        value: <PriceWithIcon value={safeStr(s?.total_book_value_inr).trim() || "-"} />,
        badge: "Net",
        sub: totalBookValue !== "-" ? `₹ ${totalBookValue}` : "Net book value",
        color: "teal",
        icon: <CurrencyRupeeOutlined className={classes.kpiIcon} />,
      },
      {
        key: "active_assets",
        label: "Active Assets",
        value: activeAssets.toLocaleString("en-IN"),
        badge: `${activeAssetsPct}%`,
        sub: "Currently active",
        color: "green",
        icon: <TaskAltOutlined className={classes.kpiIcon} />,
      },
      {
        key: "in_warranty",
        label: "In Warranty",
        value: inWarranty.toLocaleString("en-IN"),
        badge: `${inWarrantyPct}%`,
        sub: "Warranty valid",
        color: "teal",
        icon: <VerifiedOutlined className={classes.kpiIcon} />,
      },
      {
        key: "out_of_warranty",
        label: "Out of Warranty",
        value: outWarranty.toLocaleString("en-IN"),
        badge: `${outWarrantyPct}%`,
        sub: "Needs renewal",
        color: "orange",
        icon: <WarningAmberOutlined className={classes.kpiIcon} />,
      },
      {
        key: "unknown_warranty",
        label: "Unknown Warranty",
        value: unknownWarranty.toLocaleString("en-IN"),
        badge: "Check",
        sub: "Missing warranty data",
        color: "gray",
        icon: <HelpOutlineOutlined className={classes.kpiIcon} />,
      },
    ];
  }, [assetSummary]);


  return (
    <div className={classes.page}>
      <div className={classes.topHeader}>
        <HeaderTitle
          titleText={
            assetIdParam ? `Asset Register - ${assetIdParam}` : "Asset Register"
          }
        />
        {assetIdParam ? (
          <div className={classes.detailHeaderRight}>
            <SearchBar
              value={detailSearchQuery}
              onChange={setDetailSearchQuery}
              placeholder="Search within this asset..."
              customClass={classes.detailSearch}
            />
            <DropdownBtnOutlined
              label="DOWNLOAD AS"
              btnHeight="4rem"
              btnWidth="16rem"
              loadingState={exportLoading}
              onClick={(fileFormat) => exportDetails(fileFormat)}
              options={["CSV", "PDF"]}
              custom={classes.exportBtn}
            />
            <DButton
              label={"Back"}
              btnHeight="4rem"
              btnWidth={"10rem"}
              buyerBtn={true}
              customWrapperClass={classes.inlineButtonWrapper}
              onClick={closeDetails}
            />
          </div>
        ) : null}
      </div>

      {!assetIdParam ? (
        <>
          <div className={classes.kpiRow}>
            {summaryCards.map((card) => (
              <div
                key={card.key}
                className={`${classes.kpiCard} ${classes[`kpiCard_${card.color}`]}`}
              >
                <div className={classes.kpiCardHeader}>
                  <div
                    className={`${classes.kpiIconWrap} ${classes[`kpiIconWrap_${card.color}`]}`}
                  >
                    {card.icon}
                  </div>
                </div>
                <div className={classes.kpiValueRow}>
                  {assetSummaryLoading ? (
                    <span className={classes.kpiSkeletonValue} />
                  ) : (
                    <span className={classes.kpiValue}>{card.value}</span>
                  )}
                  {assetSummaryLoading ? (
                    <span className={classes.kpiSkeletonBadge} />
                  ) : (
                    <span
                      className={`${classes.kpiBadge} ${classes[`kpiBadge_${card.color}`]}`}
                    >
                      {card.badge}
                    </span>
                  )}
                </div>
                <div className={classes.kpiLabelRow}>
                  <span className={classes.kpiLabel}>{card.label}</span>
                </div>
                <div className={classes.kpiSubRow}>
                  {assetSummaryLoading ? (
                    <span className={classes.kpiSkeletonSub} />
                  ) : (
                    <span className={classes.kpiSub}>{card.sub}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {assetSummaryError ? (
            <div className={classes.kpiErrorRow}>
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ fontFamily: "gilroyMedium", fontSize: "14px" }}
              >
                {assetSummaryError}
              </Alert>
            </div>
          ) : null}
        </>
      ) : null}

      {!assetIdParam ? (
        <div className={classes.controlsBar}>
          <div className={classes.controlsRow}>
            <SearchBar
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setPageNumber(1);
              }}
              placeholder="Search Asset / Item / GRN / PO / Supplier..."
              customClass={classes.search}
            />
          </div>

          {errorMessage ? (
            <div className={classes.statusRow}>
              <Alert
                severity="error"
                variant="outlined"
                sx={{ fontFamily: "gilroyMedium", fontSize: "14px" }}
              >
                {errorMessage}
              </Alert>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={classes.grid}>
        {!assetIdParam ? (
          <div className={classes.card}>
            <div className={classes.tableWrap}>
              <DataTable
                headings={headings}
                tableData={assetRows}
                tableSize="100%"
                actions={true}
                viewBtn={true}
                editBtn={false}
                deleteBtn={false}
                sorting={true}
                viewClick={(id, row) => openDetails(row)}
                isLoading={loading}
                errorMessage={errorMessage}
                stickyHead={true}
                stickyFirstColumn={true}
                tableFixedHeight="520px"
              />
            </div>

            <div className={classes.paginationRow}>
              <PaginationComponent
                currentPage={pageNumberInt}
                itemsPerPage={paginationMeta?.current_page_count || 0}
                totalItems={paginationMeta?.page_count ?? 0}
                disableNext={pageNumberInt >= (paginationMeta?.page_count || 0)}
                disablePrevious={pageNumberInt <= 1}
                previouPageClick={() =>
                  previouPageClick(pageNumberInt, setPageNumber)
                }
                nextPageClick={() =>
                  nextPageClick(pageNumberInt, setPageNumber, paginationMeta)
                }
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
              />
            </div>
          </div>
        ) : (
          <div className={classes.card}>
            {!details ? (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ fontFamily: "gilroyMedium", fontSize: "14px" }}
              >
                {loading
                  ? "Loading asset register..."
                  : "No details found for this Asset ID."}
              </Alert>
            ) : (
              <div className={classes.sheetWrapper}>
                <table className={classes.sheetTable}>
                  <colgroup>
                    <col style={{ width: "200px" }} />
                    <col />
                    <col style={{ width: "200px" }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={classes.sheetTh}>Asset Field</th>
                      <th className={classes.sheetTh}>Asset Details</th>
                      <th className={classes.sheetTh}>Asset Field</th>
                      <th className={classes.sheetTh}>Asset Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSheetSections.flatMap((section) => {
                      const fields = Array.isArray(section?.fields) ? section.fields : [];
                      const pairs = [];
                      for (let i = 0; i < fields.length; i += 2) {
                        pairs.push([fields[i], fields[i + 1] || null]);
                      }

                      const renderValue = (field) => {
                        if (!field) return "";

                        if (field?.cellType === "disposalStatus") {
                          return (
                            <div className={classes.disposalEditor}>
                              <select
                                className={classes.disposalSelect}
                                value={disposalStatusSelection}
                                onChange={(e) => {
                                  const next = safeStr(e.target.value);
                                  setDisposalStatusSelection(next);
                                  setDisposalStatusError("");
                                  setDisposalStatusSuccess(false);
                                  if (next !== "Other") setDisposalStatusOther("");
                                }}
                              >
                                <option value="">Select...</option>
                                {DISPOSAL_STATUS_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>

                              {disposalStatusSelection === "Other" ? (
                                <input
                                  className={classes.disposalInput}
                                  value={disposalStatusOther}
                                  placeholder="Years (e.g. 7)"
                                  inputMode="numeric"
                                  maxLength={3}
                                  onChange={(e) => {
                                    const digits = safeStr(e.target.value)
                                      .replace(/\D/g, "")
                                      .slice(0, 3);
                                    setDisposalStatusOther(digits);
                                    setDisposalStatusError("");
                                    setDisposalStatusSuccess(false);
                                  }}
                                />
                              ) : null}

                              <button
                                type="button"
                                className={classes.disposalSaveBtn}
                                onClick={handleSaveDisposalStatus}
                                disabled={
                                  disposalStatusSaving ||
                                  !safeStr(disposalStatusSelection).trim() ||
                                  (safeStr(disposalStatusSelection).trim() === "Other" &&
                                    !(parseInt(safeStr(disposalStatusOther).trim(), 10) > 0))
                                }
                              >
                                {disposalStatusSaving ? "Saving..." : "Save"}
                              </button>

                              {disposalStatusSuccess ? (
                                <span className={classes.disposalSuccess}>Saved</span>
                              ) : null}

                              {disposalStatusError ? (
                                <span className={classes.disposalError}>
                                  {disposalStatusError}
                                </span>
                              ) : null}
                            </div>
                          );
                        }

                        if (field?.cellType === "insurancePolicyCopy") {
                          return (
                            <div className={classes.insuranceEditor}>
                              <div className={classes.insuranceFilePicker}>
                                <input
                                  id="insurance-policy-file"
                                  className={classes.insuranceFileInput}
                                  type="file"
                                  accept={DOCUMENT_IMAGE_UPLOAD_ACCEPT}
                                  ref={insurancePolicyFileInputRef}
                                  onChange={(e) => {
                                    const file = e?.target?.files?.[0] || null;
                                    setInsurancePolicyFile(file);
                                    setInsurancePolicyError("");
                                    setInsurancePolicyExtractSuccess(false);
                                    setInsurancePolicySaveSuccess(false);
                                  }}
                                />
                                <label
                                  htmlFor="insurance-policy-file"
                                  className={classes.insuranceChooseBtn}
                                >
                                  Choose file
                                </label>
                                <span className={classes.insuranceFileName}>
                                  {insurancePolicyFile?.name || "No file chosen"}
                                </span>
                                {insurancePolicyFile ? (
                                  <Tooltip
                                    title="Remove file"
                                    placement="top"
                                    slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                                  >
                                    <IconButton
                                      size="medium"
                                      className={classes.insuranceRemoveBtn}
                                      onClick={() => {
                                        setInsurancePolicyFile(null);
                                        setInsurancePolicyError("");
                                        setInsurancePolicyExtractSuccess(false);
                                        setInsurancePolicySaveSuccess(false);
                                        if (insurancePolicyFileInputRef.current) {
                                          insurancePolicyFileInputRef.current.value = "";
                                        }
                                      }}
                                    >
                                      <DeleteOutline fontSize="large" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </div>

                              <button
                                type="button"
                                className={classes.insuranceUploadBtn}
                                onClick={handleExtractInsurancePolicyNumber}
                                disabled={insurancePolicyExtracting || !insurancePolicyFile}
                              >
                                {insurancePolicyExtracting
                                  ? "Extracting..."
                                  : "Upload & Extract"}
                              </button>

                              {insurancePolicyExtractSuccess ? (
                                <span className={classes.disposalSuccess}>Extracted</span>
                              ) : null}
                              {insurancePolicyError ? (
                                <span className={classes.disposalError}>
                                  {insurancePolicyError}
                                </span>
                              ) : null}
                            </div>
                          );
                        }

                        if (field?.cellType === "insurancePolicyNumber") {
                          return (
                            <div className={classes.insuranceEditor}>
                              <input
                                className={classes.insuranceNumberInput}
                                value={insurancePolicyNumberDraft}
                                placeholder="Policy number"
                                readOnly
                              />

                              {(() => {
                                const savedValue = safeStr(insurancePolicyNumberSaved).trim();
                                const draftValue = safeStr(insurancePolicyNumberDraft).trim();
                                const needsSave = draftValue && draftValue !== savedValue;

                                if (needsSave) {
                                  return (
                                    <button
                                      type="button"
                                      className={classes.insuranceSaveBtn}
                                      onClick={handleSaveInsurancePolicyNumber}
                                      disabled={insurancePolicySaving || !draftValue}
                                    >
                                      {insurancePolicySaving ? "Saving..." : "Save"}
                                    </button>
                                  );
                                }

                                return savedValue ? (
                                  <span className={classes.disposalSuccess}>Saved</span>
                                ) : null;
                              })()}
                              {insurancePolicyError ? (
                                <span className={classes.disposalError}>
                                  {insurancePolicyError}
                                </span>
                              ) : null}
                            </div>
                          );
                        }

                        if (field?.cellType === "warrantyReferenceNumber") {
                          return (
                            <div className={classes.insuranceEditor}>
                              <input
                                className={classes.insuranceNumberInput}
                                value={warrantyReferenceNumberDraft}
                                placeholder="Warranty / licence number"
                                readOnly
                              />

                              {(() => {
                                const savedValue = safeStr(warrantyReferenceNumberSaved).trim();
                                const draftValue = safeStr(warrantyReferenceNumberDraft).trim();
                                const needsSave = draftValue && draftValue !== savedValue;

                                if (needsSave) {
                                  return (
                                    <button
                                      type="button"
                                      className={classes.insuranceSaveBtn}
                                      onClick={handleSaveWarrantyReferenceNumber}
                                      disabled={warrantyReferenceSaving || !draftValue}
                                    >
                                      {warrantyReferenceSaving ? "Saving..." : "Save"}
                                    </button>
                                  );
                                }

                                return savedValue ? (
                                  <span className={classes.disposalSuccess}>Saved</span>
                                ) : null;
                              })()}
                              {warrantyReferenceError ? (
                                <span className={classes.disposalError}>
                                  {warrantyReferenceError}
                                </span>
                              ) : null}
                            </div>
                          );
                        }

                        if (field?.cellType === "warrantyCard") {
                          return (
                            <div className={classes.attachmentEditor}>
                              <div className={classes.attachmentFilePicker}>
                                <input
                                  id="warranty-card-file"
                                  className={classes.attachmentFileInput}
                                  type="file"
                                  accept={DOCUMENT_IMAGE_UPLOAD_ACCEPT}
                                  ref={warrantyCardFileInputRef}
                                onChange={(e) => {
                                  const file = e?.target?.files?.[0] || null;
                                  setWarrantyCardFile(file);
                                  setWarrantyCardError("");
                                  setWarrantyCardExtractSuccess(false);
                                  setWarrantyReferenceError("");
                                  setWarrantyReferenceSaveSuccess(false);
                                }}
                              />
                                <label
                                  htmlFor="warranty-card-file"
                                  className={classes.attachmentChooseBtn}
                                >
                                  Choose file
                                </label>
                                <span className={classes.attachmentFileName}>
                                  {warrantyCardFile?.name ||
                                    warrantyCardFileNameSaved ||
                                    "No file chosen"}
                                </span>
                                {warrantyCardFile ? (
                                  <Tooltip
                                    title="Remove file"
                                    placement="top"
                                    slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                                  >
                                    <IconButton
                                      size="medium"
                                      className={classes.attachmentRemoveBtn}
                                      onClick={() => {
                                        setWarrantyCardFile(null);
                                        setWarrantyCardError("");
                                        setWarrantyCardExtractSuccess(false);
                                        if (warrantyCardFileInputRef.current) {
                                          warrantyCardFileInputRef.current.value = "";
                                        }
                                      }}
                                    >
                                      <DeleteOutline fontSize="large" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </div>

                              <button
                                type="button"
                                className={classes.attachmentExtractBtn}
                                onClick={handleExtractWarrantyOrLicenseDetails}
                                disabled={warrantyCardExtracting || !warrantyCardFile}
                              >
                                {warrantyCardExtracting ? "Extracting..." : "Extract"}
                              </button>
                              {warrantyCardExtractSuccess ? (
                                <span className={classes.disposalSuccess}>Extracted</span>
                              ) : null}
                              {warrantyCardError ? (
                                <span className={classes.disposalError}>
                                  {warrantyCardError}
                                </span>
                              ) : null}
                            </div>
                          );
                        }

                        if (field?.cellType === "price") {
                          return <PriceWithIcon value={field?.value} />;
                        }

                        return safeStr(field.value) || "-";
                      };

                      return [
                        <tr key={`${section.title}-header`} className={classes.sheetSectionRow}>
                          <td className={classes.sheetSectionHeader} colSpan={4}>
                            {section.title}
                          </td>
                        </tr>,
                        ...pairs.map(([left, right], pairIndex) => (
                          <tr key={`${section.title}-${pairIndex}`} className={classes.sheetDataRow}>
                            <td className={`${classes.sheetCell} ${classes.sheetLabelCell}`}>
                              {safeStr(left?.label) || "-"}
                            </td>
                            <td className={classes.sheetCell}>{renderValue(left)}</td>

                            <td className={`${classes.sheetCell} ${classes.sheetLabelCell}`}>
                              {right ? safeStr(right?.label) || "-" : ""}
                            </td>
                            <td className={classes.sheetCell}>
                              {right ? renderValue(right) : ""}
                            </td>
                          </tr>
                        )),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetRegisterTab;
